import { useMemo } from 'react'
import * as React from 'react'
import { Polyline } from '@react-google-maps/api'
import { useRouteDirections } from 'tripPlanner/hooks/api/routes'
import { EmptyArray } from 'lib/array/arrayUtils'
import calculateDistance from 'lib/geo/distanceBetweenPoints'
import type { TravelMode } from 'tripPlanner/api/routes/getRouteInfo'
import { DISTANCE_THRESHOLD_BETWEEN_WALK_AND_DRIVE } from 'tripPlanner/api/routes/getRouteInfo'
import { useQueryClient } from '@tanstack/react-query'
import * as RouteKeys from 'tripPlanner/hooks/reactQueryKeys/routes'
interface RoutePolylineProps {
  origin?: { lat: number, lng: number }
  destination?: { lat: number, lng: number }
  color?: string
  travelMode?: TravelMode
}

function predictMode(origin?: { lat: number, lng: number }, destination?: { lat: number, lng: number }): TravelMode | undefined {
  if (!origin || !destination) return undefined
  const km = calculateDistance(origin.lat, origin.lng, destination.lat, destination.lng)
  return km > DISTANCE_THRESHOLD_BETWEEN_WALK_AND_DRIVE ? 'DRIVE' : 'WALK'
}

function mostRecentCachedMode(
  qc: ReturnType<typeof useQueryClient>,
  origin?: { lat: number, lng: number },
  destination?: { lat: number, lng: number },
): TravelMode | undefined {
  if (!origin || !destination) return undefined
  const driveKey = RouteKeys.routeDirectionsQueryKey(origin, destination, 'DRIVE')
  const walkKey = RouteKeys.routeDirectionsQueryKey(origin, destination, 'WALK')
  const driveState = qc.getQueryState<Array<google.maps.LatLngLiteral>>(driveKey)
  const walkState = qc.getQueryState<Array<google.maps.LatLngLiteral>>(walkKey)

  const hasDrive = Array.isArray(driveState?.data) && (driveState?.data?.length ?? 0) > 0
  const hasWalk = Array.isArray(walkState?.data) && (walkState?.data?.length ?? 0) > 0

  if (hasDrive && hasWalk) {
    const driveUpdatedAt = driveState?.dataUpdatedAt ?? 0
    const walkUpdatedAt = walkState?.dataUpdatedAt ?? 0
    return driveUpdatedAt > walkUpdatedAt ? 'DRIVE' : 'WALK'
  }
  if (hasDrive) return 'DRIVE'
  if (hasWalk) return 'WALK'
  return undefined
}

function RoutePolyline({ origin, destination, color, travelMode }: RoutePolylineProps): React.ReactElement | null {
  const queryClient = useQueryClient()
  const cachedMode = mostRecentCachedMode(queryClient, origin, destination)
  const fallbackMode = predictMode(origin, destination)
  const chosenMode = travelMode ?? cachedMode ?? fallbackMode
  const { data: routePath = EmptyArray } = useRouteDirections(origin, destination, chosenMode)

  const chosenKey = RouteKeys.routeDirectionsQueryKey(origin, destination, chosenMode)
  let otherMode: TravelMode | undefined
  if (chosenMode === 'DRIVE') {
    otherMode = 'WALK'
  } else if (chosenMode === 'WALK') {
    otherMode = 'DRIVE'
  }
  const otherKey = otherMode ? RouteKeys.routeDirectionsQueryKey(origin, destination, otherMode) : undefined
  const cachedChosen = queryClient.getQueryData<Array<google.maps.LatLngLiteral>>(chosenKey)
  const cachedOther = otherKey ? queryClient.getQueryData<Array<google.maps.LatLngLiteral>>(otherKey) : undefined
  let displayPath = routePath
  if (displayPath.length === 0) {
    if (Array.isArray(cachedChosen) && cachedChosen.length > 0) {
      displayPath = cachedChosen
    } else if (Array.isArray(cachedOther) && cachedOther.length > 0) {
      displayPath = cachedOther
    }
  }

  const polylineOptions = useMemo(() => ({
    strokeColor: color,
    strokeOpacity: 0.5,
    strokeWeight: 6,
  }), [color])

  if (displayPath.length === 0) {
    return null
  }

  return (
    <Polyline
      path={displayPath}
      options={polylineOptions}
    />
  )
}

export default RoutePolyline
